import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { db, mirrorDispatches } from "../lib/db";
import { useOutbox, toQueuedDispatch, loadOutbox } from "../lib/outbox";
import { setOnline, probe } from "../lib/net";
import { withAuthRetry, isAuthError, ensureSession } from "../lib/authState";

export const DISPATCH_SELECT = "*, properties(title)";

export function useDispatches() {
  const { user } = useAuth();
  const { items } = useOutbox();
  const [rows, setRows] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!user) return;
    const cached = await db.all("dispatches");
    if (cached.length) setRows((r) => r ?? cached.sort((a, b) => b.created_at.localeCompare(a.created_at)));
    const auth = await ensureSession();
    if (!auth.session) { if (!cached.length && !auth.expired) setError(""); setFromCache(true); if (auth.offline) setOnline(false); return; }
    const { data, error } = await withAuthRetry(() => supabase.from("dispatches").select(DISPATCH_SELECT).order("created_at", { ascending: false }));
    if (error) {
      if (!cached.length && !isAuthError(error)) setError(error.message);
      if (/fetch|network/i.test(error.message) || !navigator.onLine) setOnline(false);
      setFromCache(true);
      return;
    }
    setRows(data); setFromCache(false); probe();
    mirrorDispatches(data);
    supabase.from("properties").select("*").then(({ data: p }) => p && db.putAll("properties", p));
  }, [user]);

  useEffect(() => {
    refresh(); loadOutbox();
    const onFocus = () => document.visibilityState === "visible" && refresh();
    window.addEventListener("focus", onFocus); window.addEventListener("online", refresh); window.addEventListener("dropit-synced", refresh);
    document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("focus", onFocus); window.removeEventListener("online", refresh); window.removeEventListener("dropit-synced", refresh); document.removeEventListener("visibilitychange", onFocus); };
  }, [refresh]);

  const queued = items.map(toQueuedDispatch);
  const ids = new Set((rows || []).map((r) => r.job_token));
  const merged = rows === null && !queued.length ? null : [...queued.filter((q) => !ids.has(q.job_token)), ...(rows || [])];
  return { rows: merged, error, refresh, fromCache };
}
